/**
 * Unified Message Service
 *
 * Handles message sending and real-time streaming via SSE.
 * Works with RealtimeStreamManager for true real-time updates.
 *
 * Features:
 * - Unified interface for sending messages (job queue mode)
 * - Optimistic UI updates
 * - Real-time SSE streaming via Redis pub/sub
 * - Job cancellation support
 * - Automatic reconnection and recovery
 */

import { v4 as uuidv4 } from 'uuid';
import { useMessageStore } from '../stores/messageStore';
import { useVideoWorkspaceStore } from '../stores/videoWorkspaceStore';
import api from './api';
import { logger } from '../utils/logger';
import { realtimeStreamManager } from './realtimeStreamManager';

interface ImageData {
  data: string; // base64 encoded image data
  mimeType: string;
  filename?: string;
}

/**
 * Message context for deterministic routing
 * Sent by UI to indicate which view/action is being performed
 */
export interface MessageContext {
  source: 'outline' | 'studio' | 'narration';  // Which view is sending the message
  outlineAction?: 'create' | 'edit';  // For outline view
  narrationAction?: 'generate' | 'edit';  // For narration view
  slideId?: string;  // For studio view (future)
  slideVersion?: number;  // For studio view (future)
}

interface SendMessageData {
  conversationId: string;
  content: string;
  attachments?: string[];
  images?: ImageData[];
  model?: string;
  provider?: 'gemini' | 'claude';
  placeholderMessageId?: string;
  skipUserMessage?: boolean;
  context?: MessageContext;  // UI context for deterministic routing
}

interface JobResponse {
  mode: string;
  jobId: string;
  messageId: string;
  conversationId: string;
  status: string;
  pollUrls: {
    events: string;
    status: string;
    chunks: string;
  };
  initialMessage: {
    id: string;
    role: string;
    content: string;
    timestamp: string;
    metadata: any;
  };
  createdAt: string;
}

class UnifiedMessageService {
  /**
   * Send a message to a conversation
   * Returns the user message ID
   */
  async sendMessage(data: SendMessageData): Promise<string> {
    logger.debug('📤 UnifiedMessageService: Sending message', {
      conversationId: data.conversationId,
      contentLength: data.content.length,
      hasImages: !!data.images?.length
    });

    try {
      let userMessageId: string;

      // Only create user message if not skipped (for hero page flow)
      if (!data.skipUserMessage) {
        userMessageId = uuidv4();

        // Check if user message already exists
        const existingUserMessage = useMessageStore.getState().getMessageById(userMessageId);
        if (existingUserMessage) {
          logger.warn('⚠️ UnifiedMessageService: User message already exists', { userMessageId });
          return userMessageId;
        }

        const userMessage = {
          id: userMessageId,
          conversationId: data.conversationId,
          role: 'user' as const,
          content: data.content,
          timestamp: new Date(),
          attachments: [],
          metadata: {
            streamComplete: true,
          },
          status: 'sent' as const,
          thinkingHistory: [],
          currentThinkingSubject: undefined,
          currentThinking: undefined,
        };

        // Add user message immediately for optimistic UI
        logger.debug('🆕 UnifiedMessageService: Creating user message', { userMessageId });
        useMessageStore.getState().addMessage(userMessage);
      } else {
        // User message was already created
        userMessageId = 'pre-created-' + Date.now();
        logger.debug('✅ UnifiedMessageService: Skipping user message creation (already created)');
      }

      // Handle placeholder AI message
      let placeholderAIMessageId = data.placeholderMessageId;

      // Verify the provided placeholder exists and is still a placeholder
      if (placeholderAIMessageId) {
        const existingPlaceholder = useMessageStore.getState().getMessageById(placeholderAIMessageId);

        if (existingPlaceholder && existingPlaceholder.metadata?.isPlaceholder) {
          logger.debug('✅ UnifiedMessageService: Using pre-created placeholder', { placeholderAIMessageId });
          useMessageStore.getState().setStreamingMessage(placeholderAIMessageId);
        } else {
          logger.warn('⚠️ UnifiedMessageService: Invalid placeholder, will create new one');
          placeholderAIMessageId = undefined;
        }
      }

      if (!placeholderAIMessageId) {
        // Create placeholder AI message for instant feedback
        placeholderAIMessageId = uuidv4();
      }

      // At this point placeholderAIMessageId is guaranteed to be a string
      const finalPlaceholderId: string = placeholderAIMessageId as string;

      // Only create placeholder message if we generated a new ID (not reusing existing)
      if (!data.placeholderMessageId || data.placeholderMessageId !== finalPlaceholderId) {
        const placeholderAIMessage = {
          id: finalPlaceholderId,
          conversationId: data.conversationId,
          role: 'assistant' as const,
          content: '',
          timestamp: new Date(),
          attachments: [],
          metadata: {
            isPlaceholder: true,
            streamComplete: false,
            isStreaming: true,
          },
          status: 'streaming' as const,
          thinkingHistory: [
            {
              subject: 'Processing your request',
              timestamp: Date.now(),
              completed: false
            }
          ],
          currentThinkingSubject: 'Processing your request',
          currentThinking: 'Initializing AI processing...',
        };

        logger.debug('🎯 UnifiedMessageService: Creating placeholder AI message', { placeholderAIMessageId: finalPlaceholderId });
        useMessageStore.getState().addMessage(placeholderAIMessage);
        useMessageStore.getState().setStreamingMessage(finalPlaceholderId);
      }

      // Immediately update slideConversation status for slide conversations
      // This provides instant feedback in the filmstrip before SSE events arrive
      const { slideConversations, updateSlideConversation } = useVideoWorkspaceStore.getState();
      const slideConv = slideConversations.find(sc => sc.id === data.conversationId);
      if (slideConv) {
        logger.debug('🎬 UnifiedMessageService: Updating slide status to generating', {
          outlineSlideId: slideConv.outlineSlideId,
          conversationId: data.conversationId
        });
        updateSlideConversation(slideConv.outlineSlideId, {
          status: 'generating',
          generatingThinking: 'Processing your request...',
        });
      }

      // Prepare request payload
      const payload: any = {
        content: data.content,
        attachments: data.attachments,
        model: data.model,
        provider: data.provider,
      };

      // Add images if present
      if (data.images && data.images.length > 0) {
        payload.images = data.images.map(img => ({
          data: img.data,
          mimeType: img.mimeType,
          filename: img.filename
        }));
      }

      // Add context for deterministic routing (VIDEO workflow)
      if (data.context) {
        payload.context = data.context;
        logger.debug('📋 UnifiedMessageService: Adding context to payload', {
          source: data.context.source,
          outlineAction: data.context.outlineAction
        });
      }

      // Send message to API
      logger.debug('🌐 UnifiedMessageService: Sending HTTP request to API');
      const response = await api.post<JobResponse>(
        `/conversations/${data.conversationId}/messages`,
        payload
      );

      logger.info('✅ UnifiedMessageService: Message sent, job created', {
        jobId: response.data.jobId,
        messageId: response.data.messageId,
        mode: response.data.mode
      });

      // Check response mode
      if (response.data.mode === 'async') {
        // Job queue mode - replace placeholder with actual message
        const { messageId, initialMessage } = response.data;

        logger.debug('🔄 UnifiedMessageService: Replacing placeholder with actual message', {
          placeholderId: finalPlaceholderId,
          actualId: messageId
        });

        // Get placeholder thinking before removing (hybrid approach)
        const placeholder = useMessageStore.getState().getMessageById(finalPlaceholderId);
        const placeholderThinking = {
          thinkingHistory: placeholder?.thinkingHistory || [],
          currentThinkingSubject: placeholder?.currentThinkingSubject,
          currentThinking: placeholder?.currentThinking,
        };

        // Remove placeholder
        useMessageStore.getState().removeMessage(finalPlaceholderId);

        // Add or update real message (handles race with ChatArea's loadMessages)
        // When loadMessages' API call returns before this point, it may have already
        // added this messageId to the store. addMessage skips duplicates, so we need
        // to detect that case and use updateMessage to apply streaming/thinking state.
        const existingMsg = useMessageStore.getState().getMessageById(messageId);
        if (existingMsg) {
          // Message already exists (added by ChatArea's loadMessages API call)
          // Update it with streaming state instead of silently skipping
          logger.debug('🔄 UnifiedMessageService: Message already exists, updating with streaming state', {
            messageId,
            hadThinking: !!placeholderThinking.currentThinkingSubject,
          });
          useMessageStore.getState().updateMessage(messageId, {
            status: 'streaming',
            metadata: {
              ...existingMsg.metadata,
              ...initialMessage.metadata,
              jobId: response.data.jobId,
              isStreaming: true,
              streamComplete: false,
            },
            thinkingHistory: placeholderThinking.thinkingHistory,
            currentThinkingSubject: placeholderThinking.currentThinkingSubject,
            currentThinking: placeholderThinking.currentThinking,
          });
        } else {
          // Message doesn't exist yet — add it (normal path)
          useMessageStore.getState().addMessage({
            id: messageId,
            conversationId: data.conversationId,
            role: 'assistant' as const,
            content: initialMessage.content || '',
            timestamp: new Date(initialMessage.timestamp),
            attachments: [],
            metadata: {
              ...initialMessage.metadata,
              jobId: response.data.jobId, // Explicitly preserve jobId from backend for polling recovery
              isStreaming: true,
              streamComplete: false,
            },
            status: 'streaming' as const,
            thinkingHistory: placeholderThinking.thinkingHistory,
            currentThinkingSubject: placeholderThinking.currentThinkingSubject,
            currentThinking: placeholderThinking.currentThinking,
          });
        }

        // Set as streaming message
        useMessageStore.getState().setStreamingMessage(messageId);

        // Start real-time SSE stream via RealtimeStreamManager
        realtimeStreamManager.onMessageSent(response.data);

      } else {
        // Legacy SSE mode (if still supported)
        logger.warn('⚠️ UnifiedMessageService: Unexpected response mode', {
          mode: response.data.mode
        });
        // TODO: Handle SSE if needed during transition
      }

      return userMessageId;

    } catch (error) {
      logger.error('❌ UnifiedMessageService: Failed to send message', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      // Update message store with error
      useMessageStore.getState().setError(
        error instanceof Error ? error.message : 'Failed to send message'
      );

      throw error;
    }
  }

  /**
   * Cancel a running job by messageId
   * Uses the new POST /message-jobs/:messageId/cancel endpoint
   */
  async cancelJob(messageId: string): Promise<void> {
    logger.info('🛑 UnifiedMessageService: Cancelling job', { messageId });

    try {
      const response = await api.post(`/message-jobs/${messageId}/cancel`);

      if (response.data.success) {
        logger.info('✅ UnifiedMessageService: Job cancelled', {
          messageId,
          wasProcessing: response.data.wasProcessing,
          conversationUnlocked: response.data.conversationUnlocked
        });
      }
    } catch (error: any) {
      // Handle 404 (job already completed) gracefully
      if (error?.response?.status === 404) {
        logger.info('ℹ️ UnifiedMessageService: Job not found (may have already completed)', { messageId });
        return; // Not an error condition
      }

      // Handle 409 (job cannot be cancelled)
      if (error?.response?.status === 409) {
        logger.info('ℹ️ UnifiedMessageService: Job cannot be cancelled (already completed/failed)', { messageId });
        return; // Not an error condition
      }

      logger.error('❌ UnifiedMessageService: Failed to cancel job', {
        messageId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  /**
   * Close stream for a conversation
   * Stops all active SSE streams for messages in this conversation
   */
  closeStream(conversationId: string): void {
    logger.debug('🔌 UnifiedMessageService: Close stream requested', { conversationId });

    // Find all messages for this conversation that are streaming
    const status = realtimeStreamManager.getStatus();
    const streamsToStop = status.streams.filter(s => s.conversationId === conversationId);

    for (const stream of streamsToStop) {
      realtimeStreamManager.stopStream(stream.messageId);
    }
  }

  /**
   * Check if conversation has active stream
   */
  hasActiveStream(conversationId: string): boolean {
    const status = realtimeStreamManager.getStatus();
    return status.streams.some(s => s.conversationId === conversationId);
  }

  /**
   * Get list of conversation IDs with active streams
   */
  getActiveStreams(): string[] {
    const status = realtimeStreamManager.getStatus();
    // Get unique conversation IDs
    const conversationIds = new Set(status.streams.map(s => s.conversationId));
    return Array.from(conversationIds);
  }

  /**
   * Start SSE stream for real-time message streaming
   */
  startSSEStream(conversationId: string, _streamUrl: string, _isFirstMessage: boolean = false): void {
    logger.debug('📡 UnifiedMessageService: startSSEStream called');
    // The stream is started automatically by onMessageSent, but this can be used for recovery
    const store = useMessageStore.getState();
    const streamingMessageId = store.streamingMessageId;
    if (streamingMessageId) {
      realtimeStreamManager.startStream(streamingMessageId, conversationId, 0);
    }
  }

  /**
   * Interrupt message (supports both SSE and job queue modes)
   * For video workspace slides, this also resets the outline status
   */
  async interruptMessage(conversationId: string): Promise<void> {
    logger.info('⏸️ UnifiedMessageService: Interrupting message', { conversationId });

    try {
      // Find all streaming messages for this conversation
      // Check either status OR metadata - useSlideThinkingSync sets status but not metadata
      const store = useMessageStore.getState();
      const messages = store.messagesByConversation[conversationId] || [];
      const streamingMessages = messages.filter(
        msg => msg.role === 'assistant' &&
               ((msg.status === 'sending' || msg.status === 'streaming') ||
                msg.metadata?.isStreaming)
      );

      // Check if this is a video workspace slide conversation
      // Import dynamically to avoid circular dependency
      const { useVideoWorkspaceStore } = await import('@/stores/videoWorkspaceStore');
      const videoStore = useVideoWorkspaceStore.getState();
      const slideConv = videoStore.slideConversations.find(sc => sc.id === conversationId);

      if (slideConv && videoStore.conversationId) {
        // This is a child slide conversation - use cancelSlideGeneration for SAFE stop
        // This cancels the job but keeps the child conversation and data intact
        logger.info('🛑 UnifiedMessageService: Cancelling slide generation (non-destructive)', {
          conversationId,
          outlineSlideId: slideConv.outlineSlideId,
          parentConversationId: videoStore.conversationId,
        });

        try {
          // Import outline API
          const { outlineAPI } = await import('@/services/api');

          // Call cancel endpoint - this cancels jobs but keeps data intact
          const result = await outlineAPI.cancelSlideGeneration(
            videoStore.conversationId,
            slideConv.outlineSlideId
          );

          // Update local store state with the previous status from API
          // This uses revertSlideGenerationStatus which keeps slideConversationId intact
          videoStore.revertSlideGenerationStatus(slideConv.outlineSlideId, result.previousStatus);

          // Update message status for immediate UI feedback
          for (const message of streamingMessages) {
            store.updateMessage(message.id, {
              status: 'error',
              metadata: {
                ...message.metadata,
                isStreaming: false,
                streamComplete: true,
                interrupted: true,
              }
            });
          }

          logger.info('✅ UnifiedMessageService: Slide generation cancelled (data preserved)', {
            outlineSlideId: slideConv.outlineSlideId,
            previousStatus: result.previousStatus,
          });
        } catch (error) {
          logger.error('❌ UnifiedMessageService: Failed to cancel slide generation', {
            error: error instanceof Error ? error.message : 'Unknown error'
          });
          // Fallback: try standard job cancellation using the message IDs
          for (const message of streamingMessages) {
            try {
              logger.info('🛑 UnifiedMessageService: Fallback - cancelling job directly', { messageId: message.id });
              await this.cancelJob(message.id);
              store.updateMessage(message.id, {
                status: 'error',
                metadata: {
                  ...message.metadata,
                  isStreaming: false,
                  streamComplete: true,
                  interrupted: true,
                }
              });
              logger.info('✅ UnifiedMessageService: Fallback job cancel succeeded', { messageId: message.id });
            } catch (fallbackError) {
              logger.warn('⚠️ UnifiedMessageService: Fallback job cancel also failed', {
                messageId: message.id,
                error: fallbackError instanceof Error ? fallbackError.message : 'Unknown error'
              });
            }
          }
          // Also revert local slide status since API cancel failed
          videoStore.revertSlideGenerationStatus(slideConv.outlineSlideId, 'completed');
        }
      } else {
        // Standard conversation - try job cancellation
        let jobCancelled = false;
        for (const message of streamingMessages) {
          try {
            logger.info('🛑 UnifiedMessageService: Cancelling job', { messageId: message.id });
            await this.cancelJob(message.id);

            store.updateMessage(message.id, {
              status: 'error',
              metadata: {
                ...message.metadata,
                isStreaming: false,
                streamComplete: true,
                interrupted: true,
              }
            });

            jobCancelled = true;
            logger.info('✅ UnifiedMessageService: Job cancelled', { messageId: message.id });
          } catch (error) {
            logger.error('❌ UnifiedMessageService: Failed to cancel job', {
              messageId: message.id,
              error: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        }

        // Fallback 1: Use streamingMessageId from store when no streaming messages found
        // This handles cases where the message store doesn't have the right status
        // (e.g., narration jobs, async job types)
        if (!jobCancelled && store.streamingMessageId) {
          const fallbackMessageId = store.streamingMessageId;
          try {
            logger.info('🛑 UnifiedMessageService: Cancelling job via streamingMessageId fallback', {
              messageId: fallbackMessageId,
            });
            await this.cancelJob(fallbackMessageId);
            jobCancelled = true;
            logger.info('✅ UnifiedMessageService: Job cancelled via fallback', {
              messageId: fallbackMessageId,
            });
          } catch (error) {
            logger.warn('⚠️ UnifiedMessageService: Fallback cancel failed', {
              messageId: fallbackMessageId,
              error: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        }

        // Fallback 2: Query backend for active jobs and cancel them
        // This is the last resort when local state is unreliable
        if (!jobCancelled) {
          try {
            const { conversationAPI } = await import('@/services/api');
            const activeJobsResponse = await conversationAPI.getActiveMessageJobs(conversationId);
            if (activeJobsResponse.hasActiveJob && activeJobsResponse.activeMessageId) {
              logger.info('🛑 UnifiedMessageService: Cancelling active job discovered via API', {
                messageId: activeJobsResponse.activeMessageId,
              });
              await this.cancelJob(activeJobsResponse.activeMessageId);
              jobCancelled = true;
              logger.info('✅ UnifiedMessageService: Active job cancelled via API query', {
                messageId: activeJobsResponse.activeMessageId,
              });
            }
          } catch (error) {
            logger.warn('⚠️ UnifiedMessageService: API-based cancel failed', {
              error: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        }

        // Also try SSE interrupt endpoint (for compatibility with legacy SSE mode)
        if (!jobCancelled) {
          try {
            await api.post(`/conversations/${conversationId}/messages/interrupt`);
            logger.info('✅ UnifiedMessageService: SSE interrupt sent');
          } catch (error) {
            logger.warn('⚠️ UnifiedMessageService: SSE interrupt failed', {
              error: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        }
      }

      // Stop polling for this conversation
      this.closeStream(conversationId);

      // Clear streaming message reference
      store.setStreamingMessage(null);

      // Clear narration job state to prevent useNarrationJobStatus from reconnecting
      try {
        const narrationState = videoStore.narrationJobStateByConversation?.[conversationId];
        if (narrationState?.isGenerating) {
          videoStore.clearNarrationJobState(conversationId);
          logger.debug('Cleared narration job state', { conversationId });
        }
      } catch {
        // Ignore - narration state may not exist for this conversation
      }

      logger.info('✅ UnifiedMessageService: Message interrupted', { conversationId });
    } catch (error) {
      logger.error('❌ UnifiedMessageService: Failed to interrupt message', {
        conversationId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }
}

// Singleton instance
export const unifiedMessageService = new UnifiedMessageService();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).unifiedMessageService = unifiedMessageService;
}
